import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useSupabaseAuth } from '../../providers/SupabaseAuthProvider';
import './VerificationStatusBanner.css';

/**
 * Verification Status Banner
 *
 * Displays the user's verification status with appropriate messaging and actions
 *
 * Verification States:
 * - provisional: 7-day grace period to verify email
 * - email_verified: Email confirmed, can post indefinitely
 * - restricted: Provisional period expired, must verify to post
 * - approved: Fully verified by community (manual approval)
 */
const VerificationStatusBanner = () => {
  const { user } = useSupabaseAuth();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    loadVerificationStatus();
  }, [user]);

  const loadVerificationStatus = async () => {
    try {
      // Sync email verification status first
      await supabase.rpc('sync_email_verification_status');

      // Get user's verification status
      const { data, error } = await supabase
        .from('verified_users')
        .select('verification_status, email_verified, email_verified_at, provisional_until, approval_status')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading verification status:', error);
        return;
      }

      if (data) {
        setVerificationStatus(data);
      }
    } catch (err) {
      console.error('Error in loadVerificationStatus:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });

      if (error) throw error;

      alert('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error resending verification:', error);
      alert('Failed to send verification email. Please try again later.');
    }
  };

  if (loading || !verificationStatus || dismissed) {
    return null;
  }

  const { verification_status, email_verified, provisional_until, approval_status } = verificationStatus;

  // Don't show banner if fully verified
  if (verification_status === 'email_verified' && approval_status === 'approved') {
    return null;
  }

  // On mobile, hide banner for verified users (shown in header instead)
  if (isMobile && verification_status === 'email_verified') {
    return null;
  }

  // Calculate days remaining for provisional users
  let daysRemaining = 0;
  if (verification_status === 'provisional' && provisional_until) {
    const now = new Date();
    const until = new Date(provisional_until);
    daysRemaining = Math.max(0, Math.ceil((until - now) / (1000 * 60 * 60 * 24)));
  }

  const renderBannerContent = () => {
    switch (verification_status) {
      case 'provisional':
        return (
          <div className="verification-banner verification-banner--warning">
            <div className="verification-banner__icon">⏰</div>
            <div className="verification-banner__content">
              <h3 className="verification-banner__title">Verify Your Email</h3>
              <p className="verification-banner__message">
                You have <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> left to verify your email.
                After that, you won't be able to post services until verified.
              </p>
              <p className="verification-banner__subtitle">Check your inbox for the verification email!</p>
            </div>
            <div className="verification-banner__actions">
              <button
                className="verification-banner__button verification-banner__button--primary"
                onClick={handleResendVerification}
              >
                Resend Email
              </button>
            </div>
          </div>
        );

      case 'email_verified':
        if (approval_status === 'pending') {
          return (
            <div className="verification-banner verification-banner--success">
              <div className="verification-banner__icon">✅</div>
              <div className="verification-banner__content">
                <h3 className="verification-banner__title">Email Verified!</h3>
                <p className="verification-banner__message">
                  Your email has been confirmed. You can now post services indefinitely.
                </p>
                {approval_status === 'pending' && (
                  <p className="verification-banner__subtitle">
                    Your profile is pending community approval for recognition.
                  </p>
                )}
              </div>
              <div className="verification-banner__actions">
                <button
                  className="verification-banner__button verification-banner__button--text"
                  onClick={() => setDismissed(true)}
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        }
        return null;

      case 'restricted':
        return (
          <div className="verification-banner verification-banner--error">
            <div className="verification-banner__icon">⚠️</div>
            <div className="verification-banner__content">
              <h3 className="verification-banner__title">Email Verification Required</h3>
              <p className="verification-banner__message">
                Your provisional access has expired. Please verify your email to continue posting services.
              </p>
            </div>
            <div className="verification-banner__actions">
              <button
                className="verification-banner__button verification-banner__button--primary"
                onClick={handleResendVerification}
              >
                Resend Verification Email
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return renderBannerContent();
};

export default VerificationStatusBanner;
